import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Typography from "../../../Utility/Typography";
import Label from "../../../Utility/Label/Label";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Input from "../../../Utility/Input/Input";
import Checkbox from "../../../Utility/Checkbox/Checkbox";
import CommonModal from "../../../Utility/CommonModel/CommonModal";
import ErrorMsg from "../../../Utility/ErrorMsg/ErrorMsg";
import {
  createSystemListItem,
  slugValueFromDisplayName,
  updateSystemListItem,
} from "../../../Redux/Thunks/systemListsThunk";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { showSuccess } from "../../../utils/toast";
import { SystemListItemNameSchema } from "../../../FormsValidations/Validations";

const INPUT_FIELD_CLASS =
  "w-full text-sm border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary";

/**
 * Add / edit system list row for CLIENT_TAG or VENDOR_TAG (POST /system-lists or PUT /system-lists/:id).
 */
export default function SystemListTagModal({
  isOpen,
  onClose,
  onSaved,
  listType,
  /** e.g. "Client tag" — used in titles and validation copy. */
  entityLabel,
  existingItem = null,
  /** Next `sortOrder` when creating (usually max + 1). */
  nextSortOrder = 0,
  /** When false, save is blocked (caller should only open the modal when appropriate). */
  canSubmit = true,
}) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(SystemListItemNameSchema),
    defaultValues: {
      name: "",
      isActive: true,
    },
  });

  const isEdit = Boolean(existingItem?.id);

  useEffect(() => {
    if (!isOpen) return;
    setSubmitError("");
    if (existingItem?.id) {
      reset({
        name: String(existingItem.name ?? ""),
        isActive: true,
      });
    } else {
      reset({ name: "", isActive: true });
    }
  }, [isOpen, existingItem, reset]);

  const resolvedSortOrder = () => {
    if (isEdit) {
      const n = Number(existingItem?.sortOrder);
      return Number.isFinite(n) ? n : 0;
    }
    return Number.isFinite(Number(nextSortOrder)) ? Number(nextSortOrder) : 0;
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const onSubmit = async (data) => {
    if (saving || !canSubmit) return;
    setSubmitError("");
    const trimmed = String(data.name ?? "").trim();
    const sortOrder = resolvedSortOrder();
    setSaving(true);

    try {
      if (isEdit) {
        await dispatch(
          updateSystemListItem({
            id: existingItem.id,
            name: trimmed,
            sortOrder,
          })
        ).unwrap();
      } else {
        await dispatch(
          createSystemListItem({
            listType,
            name: trimmed,
            value: slugValueFromDisplayName(trimmed),
            sortOrder,
            isActive: Boolean(data.isActive),
          })
        ).unwrap();
      }
      showSuccess(isEdit ? `${entityLabel} updated.` : `${entityLabel} created.`);
      onSaved?.();
      onClose();
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, isEdit ? "Failed to update." : "Failed to create."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? `Update ${entityLabel.toLowerCase()}` : `Add ${entityLabel.toLowerCase()}`}
      width="max-w-md"
      loading={saving}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {submitError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <Typography className="text-sm text-red-700">{submitError}</Typography>
          </div>
        ) : null}

        <div>
          <Label htmlFor={`system-list-tag-name-${listType}`} required>
            Name
          </Label>
          <Input
            id={`system-list-tag-name-${listType}`}
            type="text"
            placeholder="e.g. Urgent"
            disabled={saving}
            noWrapper
            label=""
            {...register("name")}
            className={`${INPUT_FIELD_CLASS} ${errors.name ? "border-badge-danger" : ""}`}
            error={errors.name?.message}
          />
          <ErrorMsg message={errors.name?.message} className="mt-1" />
        </div>

        {!isEdit ? (
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Checkbox
                name="isActive"
                label="Active"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                disabled={saving}
              />
            )}
          />
        ) : null}

        <div className="mt-6 flex justify-end gap-2 border-t border-border-primary pt-4">
          <Button
            variant=""
            className="btn-reset"
            type="button"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <FormSubmitButton
            type="submit"
            variant="primary"
            idleIconClass=""
            disabled={saving || !canSubmit}
            loading={saving}
            label={isEdit ? "Update" : "Create"}
          />
        </div>
      </form>
    </CommonModal>
  );
}
